import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CustomerSupport.css";
import {
    customerSupportApi,
    SEVERITY_DOT,
    SEVERITY_HELP,
    SEVERITY_SHORT,
    formatMinutes,
} from "./customerSupportApi";

/*
 * Turnaround targets — the numbers behind every SLA pill in the product.
 *
 * They used to be hardcoded in two places (a `_SLA_HOURS` dict in the backend's
 * serializers.py and an `SLA_HOURS` constant in customerSupportApi.js) which
 * drifted apart with nobody noticing. They live in `cs_sla_policy` now, so
 * changing one is a save, not a deploy.
 *
 * The measured column is the point of this screen. Setting a target from
 * memory is guessing; the p75 beside each field is what the team ALREADY
 * achieves three times out of four, which is a target people accept on the day
 * it is announced. Tighten toward the median once it holds.
 */

const showToast = (msg) => {
    let t = document.getElementById("cs-toast");
    if (!t) {
        t = document.createElement("div");
        t.id = "cs-toast";
        t.className = "cs-toast";
        document.body.appendChild(t);
    }
    t.textContent = msg;
    requestAnimationFrame(() => t.classList.add("show"));
    clearTimeout(t._hide);
    t._hide = setTimeout(() => t.classList.remove("show"), 2600);
};

/* Minutes are the stored unit (P0 first-response is measured in them), but
   nobody thinks in "1620 minutes". Edit in the unit that suits the magnitude
   and convert on the way in and out. */
const UNITS = [
    { key: "m", label: "min", mins: 1 },
    { key: "h", label: "hours", mins: 60 },
    { key: "d", label: "days", mins: 1440 },
];

const bestUnit = (mins) => {
    if (mins == null) return UNITS[1];
    if (mins % 1440 === 0 && mins >= 1440) return UNITS[2];
    if (mins % 60 === 0 && mins >= 60) return UNITS[1];
    return UNITS[0];
};

const DurationInput = ({ value, onChange, disabled, id }) => {
    const [unit, setUnit] = useState(() => bestUnit(value));
    const shown = value == null ? "" : String(Math.round((value / unit.mins) * 100) / 100);

    return (
        <div className="dur-input">
            <input
                id={id}
                type="number"
                min="1"
                step="any"
                className="nt-input"
                value={shown}
                disabled={disabled}
                onChange={(e) => {
                    const n = parseFloat(e.target.value);
                    onChange(Number.isFinite(n) ? Math.max(1, Math.round(n * unit.mins)) : null);
                }}
            />
            <select
                className="nt-select"
                value={unit.key}
                disabled={disabled}
                aria-label="Unit"
                onChange={(e) => setUnit(UNITS.find((u) => u.key === e.target.value) || UNITS[1])}
            >
                {UNITS.map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
            </select>
        </div>
    );
};

const SLASettings = () => {
    const navigate = useNavigate();

    const [policies, setPolicies] = useState([]);
    const [calendar, setCalendar] = useState(null);
    const [measured, setMeasured] = useState({});
    const [reportMeta, setReportMeta] = useState(null);
    const [drafts, setDrafts] = useState({});      // id → partial policy
    const [savingId, setSavingId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [windowDays, setWindowDays] = useState(180);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        Promise.all([
            customerSupportApi.getSlaPolicies(),
            // Non-fatal: the targets are still editable without the measured
            // column, you just have less to go on.
            customerSupportApi.getTatReport({ days: windowDays }).catch(() => null),
        ])
            .then(([policyRes, tatRes]) => {
                setPolicies(policyRes.data || []);
                setCalendar(policyRes.calendar || null);
                const rows = tatRes?.data?.rows || [];
                const m = {};
                rows.forEach((r) => { m[r.key] = r; });
                setMeasured(m);
                setReportMeta(tatRes?.data || null);
            })
            .catch((e) => setError(e.message || "Failed to load turnaround targets"))
            .finally(() => setLoading(false));
    }, [windowDays]);

    useEffect(() => { load(); }, [load]);

    const draftFor = useCallback(
        (p) => ({ ...p, ...(drafts[p.id] || {}) }),
        [drafts]
    );

    const setDraft = (id, patch) =>
        setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));

    const isDirty = (p) => {
        const d = drafts[p.id];
        if (!d) return false;
        return Object.keys(d).some((k) => d[k] !== p[k]);
    };

    /* Local mirror of the backend's own validation, so an obviously-broken
       target is caught before a round trip rather than after one. */
    const problemWith = (row) => {
        if (!row.first_response_mins || row.first_response_mins < 1) return "First response must be at least 1 minute.";
        if (!row.resolution_mins || row.resolution_mins < 1) return "Resolution must be at least 1 minute.";
        if (row.first_response_mins > row.resolution_mins) {
            return "First response can't be longer than resolution — that would mean fixing a ticket before replying to it.";
        }
        return null;
    };

    const save = async (p) => {
        const row = draftFor(p);
        const problem = problemWith(row);
        if (problem) { showToast(problem); return; }

        setSavingId(p.id);
        try {
            await customerSupportApi.updateSlaPolicy({
                id: p.id,
                first_response_mins: row.first_response_mins,
                resolution_mins: row.resolution_mins,
                clock_type: row.clock_type,
                pause_on_customer_wait: row.pause_on_customer_wait,
                notes: row.notes,
            });
            showToast(`${p.severity} target saved — tickets already open keep their current deadline`);
            setDrafts((d) => { const n = { ...d }; delete n[p.id]; return n; });
            load();
        } catch (e) {
            showToast(e.message || "Failed to save");
        } finally {
            setSavingId(null);
        }
    };

    const applyMeasured = (p) => {
        const m = measured[p.severity];
        if (!m?.suggested_target_mins) {
            showToast("Nothing measured for this tier yet — run the backfill first.");
            return;
        }
        setDraft(p.id, { resolution_mins: m.suggested_target_mins });
    };

    const globals = useMemo(
        () => policies.filter((p) => !p.server_code && p.org_id == null),
        [policies]
    );
    const overrides = useMemo(
        () => policies.filter((p) => p.server_code || p.org_id != null),
        [policies]
    );

    const nothingMeasured =
        reportMeta && reportMeta.resolved_tickets > 0 &&
        reportMeta.unmeasured_tickets >= reportMeta.resolved_tickets;

    return (
        <div className="cs-page">
            <div className="page-hdr">
                <div>
                    <div className="page-crumb">
                        Customer Support <span className="sep">›</span>{" "}
                        <span className="crumb-current">Turnaround Targets</span>
                    </div>
                    <div className="page-title">Turnaround Targets</div>
                </div>
                <div className="hdr-actions">
                    <select
                        className="nt-select"
                        value={windowDays}
                        onChange={(e) => setWindowDays(Number(e.target.value))}
                        aria-label="Measurement window"
                    >
                        <option value={30}>Measured over 30 days</option>
                        <option value={90}>Measured over 90 days</option>
                        <option value={180}>Measured over 180 days</option>
                        <option value={365}>Measured over 1 year</option>
                    </select>
                    <button className="filter-btn" onClick={() => navigate("/customer-support/tickets")}>
                        Back to tickets
                    </button>
                </div>
            </div>

            <div className="sla-intro">
                <p>
                    These are the only numbers the SLA pills, breach counters and dashboard
                    percentages run against. A target set from memory is a guess, and a guessed
                    target gets ignored the first week it's missed — so each field shows what the
                    team <strong>actually</strong> achieves beside the box you're typing in.
                </p>
                <p>
                    Set a target near the measured <strong>p75</strong>: three tickets in four
                    already meet it, which people accept on the day it's announced. Tighten toward
                    the median once it holds.
                </p>
            </div>

            {nothingMeasured && (
                <div className="sla-warn">
                    <strong>Nothing has been measured yet.</strong> The suggested column will stay
                    empty until the backfill has run. On the server:
                    <code>python manage.py backfill_cs_sla --apply</code>
                    — it measures every historical ticket from data already in the mirror, so you
                    don't have to wait to collect it.
                </div>
            )}

            {error && <div className="sla-warn error">{error}</div>}
            {loading && <div className="sla-empty">Loading targets…</div>}

            {!loading && globals.map((p) => {
                const row = draftFor(p);
                const m = measured[p.severity] || {};
                const dirty = isDirty(p);
                const problem = dirty ? problemWith(row) : null;

                return (
                    <div key={p.id} className="panel sla-card">
                        <div className="panel-hdr">
                            <span className={`sev-chip sev-${p.severity.toLowerCase()}`}>
                                <span className="chip-dot" style={{ background: SEVERITY_DOT[p.severity] }}></span>
                                {p.severity}
                            </span>
                            <span className="panel-title">{SEVERITY_SHORT[p.severity]}</span>
                            <span className="panel-sub">
                                {m.resolved_count
                                    ? `${m.resolved_count} resolved in the last ${windowDays} days`
                                    : "no resolved tickets measured"}
                            </span>
                        </div>

                        <div className="sla-def">{SEVERITY_HELP[p.severity]}</div>

                        <div className="sla-grid">
                            <div className="sla-field">
                                <label className="nt-label" htmlFor={`frt-${p.id}`}>First response</label>
                                <DurationInput
                                    id={`frt-${p.id}`}
                                    value={row.first_response_mins}
                                    disabled={savingId === p.id}
                                    onChange={(v) => setDraft(p.id, { first_response_mins: v })}
                                />
                                <div className="sla-measured">
                                    {m.first_response?.p50_display
                                        ? <>Typically <strong>{m.first_response.p50_display}</strong> · slowest 10% {m.first_response.p90_display}</>
                                        : "Not measured yet"}
                                </div>
                            </div>

                            <div className="sla-field">
                                <label className="nt-label" htmlFor={`res-${p.id}`}>Resolution</label>
                                <DurationInput
                                    id={`res-${p.id}`}
                                    value={row.resolution_mins}
                                    disabled={savingId === p.id}
                                    onChange={(v) => setDraft(p.id, { resolution_mins: v })}
                                />
                                <div className="sla-measured">
                                    {m.active?.p50_display ? (
                                        <>
                                            Typically <strong>{m.active.p50_display}</strong> · p75 {m.active.p75_display} · slowest 10% {m.active.p90_display}
                                            {m.suggested_target_mins != null && (
                                                <button
                                                    className="linkish"
                                                    style={{ marginLeft: 8 }}
                                                    disabled={savingId === p.id}
                                                    onClick={() => applyMeasured(p)}
                                                >
                                                    Use {formatMinutes(m.suggested_target_mins)}
                                                </button>
                                            )}
                                        </>
                                    ) : "Not measured yet"}
                                </div>
                            </div>

                            <div className="sla-field">
                                <label className="nt-label" htmlFor={`clk-${p.id}`}>
                                    Clock
                                    <span className="hint">when the target counts down</span>
                                </label>
                                <select
                                    id={`clk-${p.id}`}
                                    className="nt-select"
                                    value={row.clock_type}
                                    disabled={savingId === p.id}
                                    onChange={(e) => setDraft(p.id, { clock_type: e.target.value })}
                                >
                                    <option value="24x7">Round the clock</option>
                                    <option value="business">Business hours only</option>
                                </select>
                                <div className="sla-measured">
                                    {row.clock_type === "24x7"
                                        ? "Counts nights, Sundays and holidays. Right for tiers where operations are stopped."
                                        : calendar
                                            ? `${calendar.business_days_label}, ${calendar.start}–${calendar.end}, minus ${calendar.holidays.length} holiday${calendar.holidays.length === 1 ? "" : "s"}.`
                                            : "Working hours only."}
                                </div>
                            </div>

                            <div className="sla-field">
                                <label className="nt-label">Waiting on customer</label>
                                <label className="sla-check">
                                    <input
                                        type="checkbox"
                                        checked={!!row.pause_on_customer_wait}
                                        disabled={savingId === p.id}
                                        onChange={(e) => setDraft(p.id, { pause_on_customer_wait: e.target.checked })}
                                    />
                                    <span>Pause the clock</span>
                                </label>
                                <div className="sla-measured">
                                    {row.pause_on_customer_wait
                                        ? "Time between an agent's reply and the customer's next message doesn't count against this tier."
                                        : "Every hour counts, whoever's turn it was. Right for P0 — when operations are stopped, nobody cares."}
                                </div>
                            </div>
                        </div>

                        {problem && <div className="sla-warn error inline">{problem}</div>}

                        <div className="sla-actions">
                            <span className="sla-hint">
                                Saving changes the target for <strong>new</strong> tickets. Anything already
                                open keeps the deadline it was given, so last quarter's numbers don't move.
                            </span>
                            <button
                                className="filter-btn primary"
                                disabled={!dirty || !!problem || savingId === p.id}
                                onClick={() => save(p)}
                            >
                                {savingId === p.id ? "Saving…" : "Save"}
                            </button>
                        </div>
                    </div>
                );
            })}

            {!loading && overrides.length > 0 && (
                <div className="panel sla-card">
                    <div className="panel-hdr">
                        <span className="panel-title">Overrides</span>
                        <span className="panel-sub">these beat the global targets above</span>
                    </div>
                    <div className="sev-tat-table">
                        <div className="sev-tat-head">
                            <span>TIER</span><span>SCOPE</span><span>FIRST RESPONSE</span>
                            <span>RESOLUTION</span><span>CLOCK</span><span></span>
                        </div>
                        {overrides.map((p) => (
                            <div key={p.id} className="sev-tat-row">
                                <span className={`sev-chip sev-${p.severity.toLowerCase()}`}>{p.severity}</span>
                                <span className="sev-tat-n">{p.scope_label}</span>
                                <span className="sev-tat-v">{p.first_response_display}</span>
                                <span className="sev-tat-v">{p.resolution_display}</span>
                                <span className="sev-tat-v dim">{p.clock_type === "24x7" ? "24×7" : "business"}</span>
                                <span />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!loading && calendar && (
                <div className="panel sla-card">
                    <div className="panel-hdr">
                        <span className="panel-title">Working calendar</span>
                        <span className="panel-sub">what the business-hours clock counts</span>
                    </div>
                    <div className="sla-cal">
                        <div><span className="k">Days</span><span className="v">{calendar.business_days_label}</span></div>
                        <div><span className="k">Hours</span><span className="v">{calendar.start} – {calendar.end}</span></div>
                        <div><span className="k">Holidays</span><span className="v">{calendar.holidays.length} configured</span></div>
                        <div>
                            <span className="k">Wait cap</span>
                            <span className="v">{formatMinutes(calendar.wait_cap_mins)} per pause</span>
                        </div>
                    </div>
                    <div className="sla-def">
                        Set in the server's environment (<code>CS_BUSINESS_DAYS</code>,{" "}
                        <code>CS_BUSINESS_START</code>, <code>CS_BUSINESS_END</code>,{" "}
                        <code>CS_HOLIDAYS</code>) rather than here — it applies to every tier at once,
                        and changing it shifts every business-hours number in the product.
                        {calendar.holidays.length === 0 && (
                            <> <strong>No holidays are configured</strong>, so public holidays currently
                            count as working days and make every business-hours figure look shorter
                            than it was.</>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SLASettings;
